import { useCallback, useContext, useRef, useState } from "react"
import { MainContext } from "../controllers/main"
import { logger, Status } from "../services/utilities/utility"

interface ListPage<T> {
  items: T[]
  nextToken: string | null
}

interface UseListControllerConfig<T> {
  // panelStatus entry name for this list (e.g. "Badges")
  panelName: string
  // label + document used by logger()
  logLabel: string
  queryDocument: unknown
  // runs the lazy query; receives a limit for the first page or a nextToken for load-more
  fetchPage: (args: { limit?: number; nextToken?: string | null }) => Promise<{
    data: any
  }>
  // extracts { items, nextToken } from the query response
  selectResult: (data: any) => ListPage<T>
}

// Standard limit used by every list fetch in the app.
const pageLimit = () => Math.round(window.innerHeight / 74) + 10

// Encapsulates the loading / panelStatus / error / pagination boilerplate that
// every single-list controller repeats for its `getList` + `loadMore` pair.
// Search stays in the controller (filter shapes differ per domain).
export const useListController = <T>(config: UseListControllerConfig<T>) => {
  const { setError, setErrorMessage, updatePanelStatus } =
    useContext(MainContext)

  const [list, setList] = useState<T[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [updatingList, setUpdatingList] = useState<boolean>(false)
  const [nextToken, setNextToken] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState<boolean>(false)

  // keep config out of the callback deps so getList/loadMore stay stable
  const configRef = useRef(config)
  configRef.current = config

  const getList = useCallback(
    async (withLoading: boolean = true) => {
      const { panelName, logLabel, queryDocument, fetchPage, selectResult } =
        configRef.current

      if (withLoading) {
        setLoading(true)
        updatePanelStatus(panelName, { loading: true })
      }

      try {
        logger(Status.Api, `QUERY ${logLabel}`, queryDocument)
        const { data } = await fetchPage({ limit: pageLimit() })
        const page = selectResult(data)
        logger(Status.Info, logLabel, page.items)

        setList(page.items)
        setNextToken(page.nextToken)
        setHasSearched(false)
        setLoading(false)
        setUpdatingList(false)
        updatePanelStatus(panelName, { status: "success", loading: false })
      } catch (e: unknown) {
        if (e instanceof Error) {
          setError(true)
          setErrorMessage(e.message)
          logger(Status.Error, logLabel, e.message)
        }
        setLoading(false)
        setUpdatingList(false)
        updatePanelStatus(panelName, { status: "error", loading: false })
      }
    },
    [setError, setErrorMessage, updatePanelStatus]
  )

  const loadMore = useCallback(async () => {
    const { logLabel, queryDocument, fetchPage, selectResult } =
      configRef.current

    try {
      logger(Status.Api, `QUERY ${logLabel}`, queryDocument)
      const { data } = await fetchPage({ nextToken })
      const page = selectResult(data)

      setList((prev) => [...prev, ...page.items])
      setNextToken(page.nextToken)
      return true
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(true)
        setErrorMessage(e.message)
        logger(Status.Error, logLabel, e.message)
      }
      return false
    }
  }, [nextToken, setError, setErrorMessage])

  return {
    list,
    setList,
    loading,
    setLoading,
    updatingList,
    setUpdatingList,
    nextToken,
    setNextToken,
    hasSearched,
    setHasSearched,
    getList,
    loadMore,
  }
}
